import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for undo/redo functionality
 * Manages history stack with a maximum size limit
 *
 * @param {*} initialState - Initial state value
 * @param {number} maxHistorySize - Maximum number of history states to keep (default: 50)
 * @returns {Object} { state, setState, undo, redo, canUndo, canRedo, clearHistory }
 */
export function useUndoRedo(initialState, maxHistorySize = 50) {
  const [history, setHistory] = useState([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isUndoRedoAction = useRef(false);

  const state = history[currentIndex];

  // Set state and add to history
  const setState = useCallback((newState) => {
    // Skip if this is an undo/redo action
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    setHistory(prevHistory => {
      // Remove all history after current index (discard redo history)
      const newHistory = prevHistory.slice(0, currentIndex + 1);

      // Support function updaters like React's useState
      const currentState = newHistory[newHistory.length - 1];
      const resolvedState = typeof newState === 'function' ? newState(currentState) : newState;

      // Add new state
      newHistory.push(resolvedState);

      // Limit history size (keep most recent states)
      if (newHistory.length > maxHistorySize) {
        return newHistory.slice(newHistory.length - maxHistorySize);
      }

      return newHistory;
    });

    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex + 1;
      // Adjust index if we hit the max size
      return newIndex >= maxHistorySize ? maxHistorySize - 1 : newIndex;
    });
  }, [currentIndex, maxHistorySize]);

  // Undo to previous state
  const undo = useCallback(() => {
    if (currentIndex > 0) {
      isUndoRedoAction.current = true;
      setCurrentIndex(prevIndex => prevIndex - 1);
    }
  }, [currentIndex]);

  // Redo to next state
  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      setCurrentIndex(prevIndex => prevIndex + 1);
    }
  }, [currentIndex, history.length]);

  // Clear history (useful when loading a new transcript)
  const clearHistory = useCallback((newInitialState) => {
    setHistory([newInitialState || initialState]);
    setCurrentIndex(0);
    isUndoRedoAction.current = false;
  }, [initialState]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return {
    state,
    setState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    historySize: history.length
  };
}

/**
 * Hook to set up keyboard shortcuts for undo/redo
 *
 * @param {function} onUndo - Callback for undo action
 * @param {function} onRedo - Callback for redo action
 * @param {boolean} canUndo - Whether undo is available
 * @param {boolean} canRedo - Whether redo is available
 */
export function useUndoRedoKeyboard(onUndo, onRedo, canUndo, canRedo) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl (Windows/Linux) or Cmd (Mac)
      const isMod = event.ctrlKey || event.metaKey;

      // Ctrl+Z or Cmd+Z - Undo
      if (isMod && event.key.toLowerCase() === 'z' && !event.shiftKey && canUndo) {
        event.preventDefault();
        console.log('[Keyboard] Undo triggered');
        onUndo();
        return;
      }

      // Ctrl+Shift+Z or Cmd+Shift+Z - Redo
      if (isMod && event.key.toLowerCase() === 'z' && event.shiftKey && canRedo) {
        event.preventDefault();
        console.log('[Keyboard] Redo triggered');
        onRedo();
        return;
      }

      // Alternative: Ctrl+Y or Cmd+Y - Redo (Windows convention)
      if (isMod && event.key.toLowerCase() === 'y' && canRedo) {
        event.preventDefault();
        console.log('[Keyboard] Redo triggered (Ctrl+Y)');
        onRedo();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onUndo, onRedo, canUndo, canRedo]);
}

export default useUndoRedo;
